import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit, User as UserIcon, Activity, Shield, Monitor, ToggleLeft, ToggleRight, CheckCircle, XCircle } from 'lucide-react';
import StatusBadge from '../../components/common/StatusBadge';
import Tabs, { type Tab } from '../../components/common/Tabs';
import UserModal from '../../components/admin/UserModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import { userService, activityService } from '../../services';
import type { User, RoleAssignment } from '../../services';
import type { ActivityRecord } from '../../services/activity.service';
import { USER_TYPES } from '../../utils/constants';
import { formatPermissions, type Permission } from '../../utils/permissions';
import { useToast } from '../../components/common/ToastContainer';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuthStore } from '../../stores/authStore';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { canUpdate } = usePermissions();
  const currentUser = useAuthStore((s) => s.user);
  const isSelf = currentUser?.id === id;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isToggleStatusModalOpen, setIsToggleStatusModalOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [userRoles, setUserRoles] = useState<RoleAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchUserRoles = useCallback(async (userId: string) => {
    try {
      const roles = await userService.getUserRoles(userId);
      setUserRoles(roles);
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    if (!id) return;

    const fetchUser = async () => {
      try {
        setLoading(true);
        const userData = await userService.getById(id);
        setUser(userData);
        // Fetch roles for TenantAdmins
        if (userData.type === USER_TYPES.TENANT_ADMIN) {
          await fetchUserRoles(id);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load user details');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, fetchUserRoles]);

  const handleSaveUser = async (updatedUser: any) => {
    if (!id) return;
    try {
      await userService.update(id, {
        userRealName: updatedUser.userRealName,
        phone: updatedUser.phone,
        email: updatedUser.email,
        status: updatedUser.status,
      });
      const userData = await userService.getById(id);
      setUser(userData);
      // Refetch roles (may have changed via UserModal)
      if (userData.type === USER_TYPES.TENANT_ADMIN) {
        await fetchUserRoles(id);
      }
      setIsEditModalOpen(false);
    } catch (err: any) {
      toast.error('Update Failed', `Failed to update user: ${err.message}`);
    }
  };

  const handleToggleStatus = async () => {
    if (!user || !id) return;
    try {
      setIsUpdating(true);
      const newStatus = user.status === 0 ? 1 : 0;
      await userService.update(id, { status: newStatus });
      const userData = await userService.getById(id);
      setUser(userData);
      setIsToggleStatusModalOpen(false);
    } catch (err: any) {
      toast.error('Update Failed', `Failed to update status: ${err.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const userForModal = user ? {
    id: user.id,
    username: user.username,
    email: user.email || '',
    userRealName: user.userRealName || '',
    phone: user.phone || '',
    type: user.type,
    status: user.status || 0,
    tenantId: user.tenantId,
  } : undefined;

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading user details...</div>;
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error || 'User not found'}</div>
      </div>
    );
  }

  const userTypeLabel =
    user.type === USER_TYPES.SUPER_ADMIN
      ? 'Super Admin'
      : user.type === USER_TYPES.CHANNEL_ADMIN
        ? 'Channel Admin'
        : user.type === USER_TYPES.TENANT_ADMIN
          ? 'Tenant Admin'
          : 'User';
  const userStatus = user.status === 0 ? 'Active' : 'Disabled';

  // Roles are tenant-scoped. SuperAdmins and ChannelAdmins have no tenant of
  // their own, so they have no role and no tenant-scoped permission set —
  // rendering the Permissions tab for them would just show an empty grid.
  const hasNoTenantScope =
    user.type === USER_TYPES.SUPER_ADMIN || user.type === USER_TYPES.CHANNEL_ADMIN;

  // Tab 1: Profile Information
  const ProfileTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Username</label>
              <p className="text-gray-900">{user.username}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Full Name</label>
              <p className="text-gray-900">{user.userRealName || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="text-gray-900">{user.email || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Phone</label>
              <p className="text-gray-900">{user.phone || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Access & Status</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">User Type</label>
              <p className="text-gray-900">{userTypeLabel}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <div className="mt-1">
                <StatusBadge status={userStatus} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Tenant</label>
              {(user as any).tenantName ? (
                <p className="text-gray-900">{(user as any).tenantName}</p>
              ) : user.type === USER_TYPES.SUPER_ADMIN ? (
                <p className="text-gray-500 italic">(system-wide)</p>
              ) : user.type === USER_TYPES.CHANNEL_ADMIN ? (
                <p className="text-gray-500 italic">(channel-wide)</p>
              ) : (
                <p className="text-gray-500 italic">Not assigned</p>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Timestamps</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Created</label>
              <p className="text-gray-900">{user.createTime || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Updated</label>
              <p className="text-gray-900">{user.updateTime || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Created By</label>
              <p className="text-gray-900">{user.createBy || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Tab 2: Activity Log (real data from API)
  const ActivityTab = () => {
    const [activities, setActivities] = useState<ActivityRecord[]>([]);
    const [activitiesLoading, setActivitiesLoading] = useState(true);
    const [activityTotal, setActivityTotal] = useState(0);

    const fetchActivities = async (page = 1) => {
      if (!id) return;
      try {
        setActivitiesLoading(true);
        const result = await activityService.getByUser(id, { page, pageSize: 20 });
        setActivities(result.items || []);
        setActivityTotal(result.pagination?.total || 0);
      } catch {
        setActivities([]);
      } finally {
        setActivitiesLoading(false);
      }
    };

    useEffect(() => { fetchActivities(); }, [id]);

    const handleExportCSV = async () => {
      if (!id) return;
      try {
        const response = await activityService.exportCSV(id);
        const blob = response instanceof Blob ? response : new Blob([response], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activity_${id}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        toast.error('Export Failed', 'Failed to export activity log');
      }
    };

    const actionColors: Record<string, string> = {
      create: 'bg-green-100 text-green-700',
      update: 'bg-blue-100 text-blue-700',
      delete: 'bg-red-100 text-red-700',
      login: 'bg-purple-100 text-purple-700',
      logout: 'bg-gray-100 text-gray-700',
      revoke: 'bg-orange-100 text-orange-700',
      assign: 'bg-teal-100 text-teal-700',
      unassign: 'bg-amber-100 text-amber-700',
      sync: 'bg-indigo-100 text-indigo-700',
    };

    const activityColumns: Column<ActivityRecord>[] = [
      {
        key: 'created_at', label: 'Timestamp', sortable: true,
        render: (a) => <span className="text-sm">{a.created_at ? new Date(a.created_at).toLocaleString() : '-'}</span>,
      },
      {
        key: 'action', label: 'Action', sortable: true,
        render: (a) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${actionColors[a.action] || 'bg-gray-100 text-gray-700'}`}>{a.action}</span>,
      },
      { key: 'resource_type', label: 'Resource Type', sortable: true, render: (a) => <span className="text-sm">{a.resource_type}</span> },
      {
        key: 'resource_id', label: 'Resource',
        render: (a) => <span className="text-sm text-gray-600">{a.resource_name || a.resource_id || '-'}</span>,
      },
      { key: 'ip_address', label: 'IP', render: (a) => <span className="text-sm font-mono">{a.ip_address || '-'}</span> },
      {
        key: 'status', label: 'Status', sortable: true,
        render: (a) => a.status === 'success' ? (
          <span className="inline-flex items-center space-x-1 text-success-700"><CheckCircle className="w-4 h-4" /><span>Success</span></span>
        ) : (
          <span className="inline-flex items-center space-x-1 text-red-600"><XCircle className="w-4 h-4" /><span>Failed</span></span>
        ),
      },
    ];

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">{activityTotal} total events</p>
          <button onClick={handleExportCSV} className="btn-secondary text-sm inline-flex items-center space-x-1">
            <span>Export CSV</span>
          </button>
        </div>
        <DataTable
          columns={activityColumns}
          data={activities}
          searchPlaceholder="Search activity..."
          loading={activitiesLoading}
        />
      </div>
    );
  };

  // Tab 3: Permissions (real data from API)
  const currentRole = userRoles.length > 0 ? userRoles[0] : null;
  const rolePermissions: string[] = currentRole?.permissions || [];

  const PermissionsTab = () => {
    // SuperAdmins bypass all role checks
    if (user.type === USER_TYPES.SUPER_ADMIN) {
      return (
        <div className="space-y-6">
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="w-6 h-6 text-amber-500" />
              <h3 className="text-lg font-semibold text-gray-900">Super Administrator</h3>
            </div>
            <p className="text-gray-600">
              This user is a Super Administrator and bypasses all role-based permission checks.
              They have full access to all resources across all tenants.
            </p>
          </div>
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Effective Permissions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(['create', 'read', 'update', 'delete'] as Permission[]).map((permission) => (
                <div key={permission} className="p-4 border rounded-lg border-success-300 bg-success-50">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-success-600" />
                    <span className="font-medium text-success-900">
                      {permission.charAt(0).toUpperCase() + permission.slice(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Role Assignment</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Current Role</label>
              <div className="mt-2">
                {currentRole ? (
                  <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                    currentRole.roleType === 'Admin' ? 'bg-red-100 text-red-700' :
                    currentRole.roleType === 'Editor' ? 'bg-blue-100 text-blue-700' :
                    currentRole.roleType === 'Viewer' ? 'bg-green-100 text-green-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {currentRole.roleName} ({currentRole.roleType})
                  </span>
                ) : (
                  <span className="text-gray-500 text-sm">No role assigned</span>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Effective Permissions</label>
              <p className="text-gray-900 mt-1">
                {rolePermissions.length > 0 ? formatPermissions(rolePermissions) : 'None'}
              </p>
            </div>
            <p className="text-xs text-gray-500">
              To change this user's role, click "Edit Info" above.
            </p>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Permission Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(['create', 'read', 'update', 'delete'] as Permission[]).map((permission) => {
              const has = rolePermissions.includes(permission);
              return (
                <div key={permission} className={`p-4 border rounded-lg ${has ? 'border-success-300 bg-success-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center space-x-2">
                    {has ? (
                      <CheckCircle className="w-5 h-5 text-success-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400" />
                    )}
                    <span className={`font-medium ${has ? 'text-success-900' : 'text-gray-500'}`}>
                      {permission.charAt(0).toUpperCase() + permission.slice(1)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Tab 4: Sessions (real data from API)
  const SessionsTab = () => {
    const [sessions, setSessions] = useState<any[]>([]);
    const [sessionsLoading, setSessionsLoading] = useState(true);
    const [sessionPage, setSessionPage] = useState(1);
    const [sessionTotal, setSessionTotal] = useState(0);
    const [revokingAll, setRevokingAll] = useState(false);

    const fetchSessions = async (page = 1) => {
      if (!id) return;
      try {
        setSessionsLoading(true);
        const result = await userService.getUserSessions(id, { page, pageSize: 20 });
        setSessions(result.items || []);
        setSessionTotal(result.pagination?.total || 0);
        setSessionPage(page);
      } catch {
        setSessions([]);
      } finally {
        setSessionsLoading(false);
      }
    };

    useEffect(() => { fetchSessions(); }, [id]);

    const handleRevokeAll = async () => {
      if (!id) return;
      try {
        setRevokingAll(true);
        const result = await userService.revokeAllSessions(id);
        toast.success('Sessions Revoked', `Revoked ${result.revoked} active sessions`);
        fetchSessions(sessionPage);
      } catch {
        toast.error('Failed', 'Failed to revoke sessions');
      } finally {
        setRevokingAll(false);
      }
    };

    const handleExportCSV = async () => {
      if (!id) return;
      try {
        const response = await userService.exportSessionsCSV(id);
        const blob = response instanceof Blob ? response : new Blob([response], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sessions_${id}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        toast.error('Export Failed', 'Failed to export sessions');
      }
    };

    const truncateUA = (ua: string | null) => {
      if (!ua) return 'Unknown';
      return ua.length > 60 ? ua.substring(0, 60) + '...' : ua;
    };

    const sessionColumns: Column<any>[] = [
      { key: 'ipAddress', label: 'IP Address', sortable: true, render: (s) => <span className="font-mono text-sm">{s.ipAddress || '-'}</span> },
      { key: 'userAgent', label: 'Device', sortable: false, render: (s) => <span className="text-sm text-gray-600" title={s.userAgent || ''}>{truncateUA(s.userAgent)}</span> },
      { key: 'createdAt', label: 'Created', sortable: true, render: (s) => <span className="text-sm">{s.createdAt ? new Date(s.createdAt).toLocaleString() : '-'}</span> },
      { key: 'lastActivity', label: 'Last Activity', sortable: true, render: (s) => <span className="text-sm">{s.lastActivity ? new Date(s.lastActivity).toLocaleString() : '-'}</span> },
      { key: 'expiresAt', label: 'Expires', sortable: true, render: (s) => <span className="text-sm">{s.expiresAt ? new Date(s.expiresAt).toLocaleString() : '-'}</span> },
      {
        key: 'status', label: 'Status', sortable: true,
        render: (s) => (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            s.status === 'Active' ? 'bg-success-100 text-success-700' :
            s.status === 'Revoked' ? 'bg-red-100 text-red-700' :
            'bg-gray-100 text-gray-700'
          }`}>{s.status}</span>
        ),
      },
    ];

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">{sessionTotal} total sessions</p>
          <div className="flex space-x-2">
            <button onClick={handleExportCSV} className="btn-secondary text-sm inline-flex items-center space-x-1">
              <span>Export CSV</span>
            </button>
            {canUpdate && (
            <button
              onClick={handleRevokeAll}
              disabled={revokingAll}
              className="btn-secondary text-sm text-red-600 border-red-200 hover:bg-red-50 inline-flex items-center space-x-1"
            >
              <span>{revokingAll ? 'Revoking...' : 'Revoke All Active'}</span>
            </button>
            )}
          </div>
        </div>
        <DataTable
          columns={sessionColumns}
          data={sessions}
          searchPlaceholder="Search by IP or user agent..."
          loading={sessionsLoading}
        />
      </div>
    );
  };

  const tabs: Tab[] = [
    { id: 'profile', label: 'Profile Information', icon: UserIcon, content: <ProfileTab key="profile" /> },
    { id: 'activity', label: 'Activity Log', icon: Activity, content: <ActivityTab key="activity" /> },
    ...(!hasNoTenantScope ? [
      { id: 'permissions', label: 'Permissions', icon: Shield, content: <PermissionsTab key="permissions" /> },
    ] : []),
    { id: 'sessions', label: 'Sessions', icon: Monitor, content: <SessionsTab key="sessions" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{user.userRealName || user.username}</h1>
            <p className="text-gray-600 mt-1">{user.email || user.username} • {userTypeLabel}</p>
          </div>
          <StatusBadge status={userStatus} />
        </div>
        <div className="flex items-center space-x-3">
          {/* This user's Secure Access history (design 19 §7.5, §9a).
              Keyed on the EMAIL, because that is the IdP subject the gateway
              verifies and records — the username is this system's own handle
              for the person and appears nowhere in the audit stream. A user
              with no email has no IdP identity to look up, so the link is not
              rendered rather than rendered onto an empty page. */}
          {user.email && (
          <Link
            to={`/access/activity?user=${encodeURIComponent(user.email)}`}
            className="btn-secondary inline-flex items-center space-x-2"
            title="View this user's Secure Access activity"
            data-testid="user-access-activity-link"
          >
            <Activity className="w-4 h-4" />
            <span>Access activity</span>
          </Link>
          )}
          {canUpdate && (
          <button onClick={() => setIsEditModalOpen(true)} className="btn-secondary inline-flex items-center space-x-2">
            <Edit className="w-4 h-4" />
            <span>Edit Info</span>
          </button>
          )}
          {canUpdate && !isSelf && (
          <button onClick={() => setIsToggleStatusModalOpen(true)} className="btn-primary inline-flex items-center space-x-2">
            {user.status === 0 ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
            <span>{user.status === 0 ? 'Deactivate' : 'Activate'}</span>
          </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <Tabs tabs={tabs} />
      </div>

      {/* Edit User Modal */}
      {userForModal && (
        <UserModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveUser}
          user={userForModal}
          mode="edit"
        />
      )}

      {/* Toggle Status Confirmation */}
      <ConfirmDialog
        isOpen={isToggleStatusModalOpen}
        onClose={() => setIsToggleStatusModalOpen(false)}
        onConfirm={handleToggleStatus}
        title={user.status === 0 ? 'Deactivate User' : 'Activate User'}
        message={`Are you sure you want to ${user.status === 0 ? 'deactivate' : 'activate'} "${user.userRealName || user.username}"?\n\n${user.status === 0 ? 'The user will no longer be able to log in.' : 'The user will be able to log in again.'}`}
        confirmText={user.status === 0 ? 'Deactivate' : 'Activate'}
        cancelText="Cancel"
        variant={user.status === 0 ? 'danger' : 'info'}
        isLoading={isUpdating}
      />
    </div>
  );
};

export default UserDetail;
